<template>
  <div class="splash-screen">
    <div class="content">
      <div class="logo-container">
        <img src="../assets/poolit-logo.svg" alt="Poolit Logo" class="logo" />

        <div class="loading-indicator">
          <div class="loading-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>

      <div class="loading-text">Loading your experience...</div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';

onMounted(() => {
  document.body.classList.add('splash-active');
  return () => document.body.classList.remove('splash-active');
});
</script>

<style scoped>
/* Import fonts */
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;700&display=swap');

/* Variables from brand system */
:root {
  --brand-purple-soft: rgba(142, 91, 254, 0.8);
  --deep-indigo-soft: rgba(49, 0, 111, 0.9);
  --brand-transition: cubic-bezier(0.4, 0, 0.2, 1);
  --loading-duration: 2s;
}

.splash-active {
  overflow: hidden;
}

/* Stretched by `inset: 0` rather than sized by a height, which is what a
   full-bleed overlay actually wants: `position: fixed` resolves both the inset
   and any percentage against the viewport, but a bare `height: 100%` with no
   bottom edge would resolve against the SMALL viewport and leave a gap under
   the splash whenever the URL bar is retracted. Mirrors the launch pages. */
.splash-screen {
  position: fixed;
  inset: 0;
  background: #8e5bfe;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Noto Sans Arabic', sans-serif;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 360px;
  padding: 20px;
}

.logo-container {
  position: relative;
  width: 160px;
  height: 160px;
  margin-bottom: 40px;
  animation: gentleFloat 2s ease-in-out infinite;
}

.logo {
  width: 100%;
  height: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
}

.loading-indicator {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.loading-dots {
  display: flex;
  gap: 6px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background-color: white;
  border-radius: 50%;
  opacity: 0.4;
  animation: pulse 1s ease-in-out infinite;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

.loading-text {
  color: white;
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
  opacity: 0.9;
  margin-top: 16px;
}

@keyframes gentleFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* Mobile Optimization */
@media (max-width: 375px) {
  .logo-container {
    width: 120px;
    height: 120px;
    margin-bottom: 30px;
  }

  .loading-text {
    font-size: 1rem;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .logo-container,
  .loading-dots span {
    animation: none;
  }

  .loading-dots span {
    opacity: 0.8;
  }
}
</style>
